// "Top Creators" — the Home panel entry listing the creators the catalog is
// built from, each opening onto everything of theirs in it.
//
// The list is derived from the catalog, not written down: every author with at
// least LISTED_MIN clips, ranked by how many. spotlights.json only adds the
// editorial extras — a real name and a bio — for the few who have been written
// up; everyone else is listed by handle. So a creator appears here as soon as
// their second clip lands, with no data entry.
//
// Creators only, deliberately. A by-position view was built and dropped: it was
// a filtered technique list, which is what Library already is. A creator has
// something Library cannot show — a face, a bio, a body of work — so it earns
// its own screen.
//
// No bio here on purpose. A bio is the creator's own words about themselves,
// rewritten and republished in Gnosis's voice about a real person who never
// agreed to it — and it goes stale silently. The portrait, the handle and their
// actual clips are all things we can stand behind. The bios stay in
// spotlights.json for the Instagram posters, which get eyeballed before they go
// out.
//
// Same creators as the Instagram spotlights (src/data/spotlights.json;
// scripts/make-reels.mjs builds the posters from the same file, and its
// `roundups` still drive the category posters even though nothing here reads
// them), but this is real UI rather than a picture of one: it reflows for a
// phone, carries no trial pitch, and every technique taps through to Library.
// The poster's three cells were an export limit, so a creator page here lists
// everything of theirs instead — as the same TechniqueTile grid Library uses,
// wired to the same play/star/info handlers, so a creator page behaves like the
// library filtered to them rather than like a separate list that looks similar.
//
// Counts come off the live catalog on every render — the size of the library is
// never written down.
import { legalityNote, ruleFor, useRules } from '../utils/legality';
import { creatorPlatform, profileUrlFor, type VideoPlatform } from '../utils/video';
import { useState } from 'react';
import { DetailPage } from './Forum';
import { Grid2x2, Rows3, Star } from 'lucide-react';
import TechniqueTile from './TechniqueTile';
import TechniqueCard from './TechniqueCard';
import { TIER_ACCENT_COLORS } from '../constants/layout';
import { useTechniques } from '../hooks/useTechniques';
import { useRankedCreators } from '../hooks/useRankedCreators';
import { groupTechniques, type TechniqueGroup } from '../utils/groupTechniques';
import spotlights from '../data/spotlights.json';
import { TechniqueStatus, type Technique, type BeltRank } from '../types';

/** The Library behaviour a creator page borrows, handed down from the shell so
 *  a tile here does the same thing a tile there does. */
export interface LibraryBridge {
  /** Library's own grid/list preference, shared rather than duplicated — the
   *  toggle here is the same setting, so it persists across both screens. */
  view: 'grid' | 'list';
  onToggleView: () => void;
  statusOf: (id: string) => TechniqueStatus;
  isWatched: (t: Technique) => boolean;
  /** Clip URLs in the order they were last played, most recent first — the
   *  Recently watched rail. Optional: only Home needs it. */
  recentlyWatched?: string[];
  onPlay: (t: Technique) => void;
  onToggleMat: (t: Technique) => void;
  onInfo: (t: Technique) => void;
  /** Open the version chooser for a technique this creator filmed twice. Rare
   *  here — one creator's page is already narrowed to one creator — but the
   *  same gi/no-gi pair that groups in Library groups here too. */
  onVersions: (g: TechniqueGroup) => void;
  /** Row props for "the library has versions this view is hiding" — a creator
   *  page is exactly where that happens, since grouping has nothing to collapse
   *  once the list is one person's clips. */
  /** Desktop-only: every version of a group as a grid of PiP players. */
  onOpenAllVersions: (g: TechniqueGroup) => void;
}

interface CreatorSpotlight {
  handle: string;
  title: string;
  // Optional: name-only rows come from scripts/backfill-creators.mjs so a
  // creator reads as a person rather than a handle. The editorial copy below
  // is hand-written, and only the Instagram posters consume it.
  authorSub?: string;
  bio?: string[];
}
const CREATORS = spotlights.creators as CreatorSpotlight[];
const WRITTEN_UP = new Map(CREATORS.map((c) => [c.handle, c]));


const portraitFor = (handle: string) => `${import.meta.env.BASE_URL}portraits/${handle}.jpg`;

/** The Library grid, filtered to one creator. Same tile and same handlers, so
 *  playing, starring and details work exactly as they do in Library. */
/** A creator's portrait, or the same box empty where no photo has been fetched
 *  for them — reserving the space keeps the rows aligned either way. */
/** The site a handle belongs to, drawn in lucide's stroke style (lucide itself
 *  carries no brand icons). Sits before the handle on a creator's page, so a
 *  YouTube channel or a TikTok account isn't labelled as an Instagram one —
 *  the mark and the link it sits in have to agree. Instagram is the fallback
 *  for a handle with no clips to judge by.
 *
 *  Each mark keeps its own brand colour, since that is most of what makes a
 *  logo readable at 18px. */
export function PlatformGlyph({ platform }: { platform: VideoPlatform | null }) {
  const props = {
    className: 'sp-handle-mark', width: 18, height: 18, viewBox: '0 0 24 24',
    fill: 'none', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
    'aria-hidden': true,
  } as const;
  if (platform === 'youtube') {
    return (
      <svg {...props} stroke="#ff0033">
        <rect x="2" y="5" width="20" height="14" rx="4" />
        <path d="M10 9.5v5l4.5-2.5z" fill="#ff0033" stroke="none" />
      </svg>
    );
  }
  if (platform === 'tiktok') {
    return (
      <svg {...props} stroke="url(#tt-gradient)">
        <defs>
          {/* TikTok's two brand colours, cyan into pink. */}
          <linearGradient id="tt-gradient" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="#25f4ee" />
            <stop offset="1" stopColor="#fe2c55" />
          </linearGradient>
        </defs>
        {/* The note: a stem down the right into a round head, with the flag
            hooking off the top. */}
        <path d="M13.5 3v11a4 4 0 1 1-4-4c.4 0 .8.06 1.2.18" />
        <path d="M13.5 3c.5 2.7 2.6 4.7 5.2 5" />
      </svg>
    );
  }
  return (
    <svg {...props} stroke="url(#ig-gradient)">
      <defs>
        {/* Instagram's brand gradient: yellow at the bottom-left through
            pink to purple at the top-right. */}
        <linearGradient id="ig-gradient" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#f9ce34" />
          <stop offset="0.5" stopColor="#ee2a7b" />
          <stop offset="1" stopColor="#6228d7" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.6" fill="url(#ig-gradient)" />
    </svg>
  );
}

export function Face({ handle, className }: { handle: string; className: string }) {
  const [broken, setBroken] = useState(false);
  if (broken) return <span className={`${className} ${className}--none`} aria-hidden="true" />;
  return (
    <img
      className={className}
      src={portraitFor(handle)}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}

/** A creator's techniques, in the Library grid. Same tile and same handlers, so
 *  playing, starring and details work exactly as they do in Library. */
export function TechniqueGrid({ list, library, lead, stars, className = 'sp-grid' }: {
  list: Technique[];
  library?: LibraryBridge;
  /** Sits in the grid ahead of the tiles, spanning its own cells. */
  lead?: React.ReactNode;
  /** Star counts by technique id, for the grids that show them (Popular). A
   *  tile whose id isn't in here carries no number. */
  stars?: Map<string, number>;
  /** Which grid this is: the spotlight page's, or Home's four-across feed. */
  className?: string;
}) {
  return (
    <div className={`technique-grid ${className}`}>
      {lead}
      {groupTechniques(list).map((g) => { const t = g.lead; const n = stars?.get(t.id); const tile = (
        <TechniqueTile
          key={g.key}
          id={t.id}
          name={t.name}
          status={library?.statusOf(t.id) ?? TechniqueStatus.Unchecked}
          categoryColor={TIER_ACCENT_COLORS[t.tier as BeltRank]}
          instagramUrl={t.instagramUrl}
          thumbnail={t.thumbnail}
          watched={library?.isWatched(t) ?? false}
          onPipClick={library ? () => library.onPlay(t) : undefined}
          onToggleMat={library ? () => library.onToggleMat(t) : undefined}
          onInfoClick={library ? () => library.onInfo(t) : undefined}
          variantCount={g.variants.length}
          onVariantsClick={library ? () => library.onVersions(g) : undefined}
          onOpenAllVersions={library ? () => library.onOpenAllVersions(g) : undefined}
        />
      ); return n === undefined ? tile : (
        /* The star count needs a positioned box of its own around the tile. */
        <div className="grid-cell" key={g.key}>
          {tile}
          <span className="rail-stars"><Star size={11} fill="currentColor" />{n}</span>
        </div>
      ); })}
    </div>
  );
}

/** The top few faces, overlapped, for the Top Creators row on Home: a hint of
 *  who's inside before the row is opened. Only creators with a portrait — a
 *  blank circle in a stack reads as broken, not as "no photo yet" — so a few
 *  more are tried than shown and the ones whose image fails drop out. */
export function CreatorFaces({ max = 3 }: { max?: number }) {
  const { techniques } = useTechniques();
  const ranked = useRankedCreators(techniques);
  const [missing, setMissing] = useState<Set<string>>(() => new Set());
  const shown = ranked
    .map(([handle]) => handle)
    .filter((h) => !missing.has(h))
    .slice(0, max);
  if (shown.length === 0) return null;
  return (
    <span className="sp-stack" aria-hidden="true">
      {shown.map((h) => (
        <img
          key={h}
          className="sp-stack-face"
          src={portraitFor(h)}
          alt=""
          loading="lazy"
          onError={() => setMissing((prev) => new Set(prev).add(h))}
        />
      ))}
    </span>
  );
}

export default function Spotlights({
  title,
  onBack,
  library,
  icon,
  initialCreator,
}: {
  title: string;
  onBack: () => void;
  library?: LibraryBridge;
  icon?: React.ReactNode;
  /** Open straight onto this creator's page (from the Home rail); back from
   *  there leaves the screen rather than landing on the list. */
  initialCreator?: string;
}) {
  const rules = useRules();
  const { techniques, loading } = useTechniques();
  // The catalog needs a signed-in read. Until it lands, rows show their name
  // and nothing else — "0 techniques" would read as an empty library rather
  // than one that hasn't loaded.
  const counted = !loading && techniques.length > 0;
  const [openId, setOpenId] = useState<string | null>(initialCreator ?? null);

  const ranked = useRankedCreators(techniques);

  // Listed creators come from the ranking; a poster with one clip isn't
  // listed, but their handle is still a link (a credit, a PiP header), so
  // their page opens on whatever they have — and on nothing at all when the
  // clip that credited them isn't in the public catalog (a pending or
  // archived one). An asked-for handle always gets a page; landing back on
  // the list reads as the tap having done nothing.
  const wanted = openId?.trim().toLowerCase();
  const openEntry: [string, Technique[]] | null = openId
    ? ranked.find(([h]) => h.trim().toLowerCase() === wanted)
      ?? [openId, techniques.filter((t) => t.author?.trim().toLowerCase() === wanted)]
    : null;
  const creator = openEntry
    ? { handle: openEntry[0], list: openEntry[1], ...WRITTEN_UP.get(openEntry[0]) }
    : null;

  if (creator) {
    const list = creator.list;
    const grid = library?.view !== 'list';
    // Their clips are what say where they post; nothing else records it.
    const platform = creatorPlatform(list);
    // Face and handle: a grid cell beside the first clip, or a full-width band
    // over the list. Same content either way.
    const head = (
      <div className={`sp-creator-cell${grid ? '' : ' sp-creator-cell--wide'}`}>
        <Face className="sp-creator-face" handle={creator.handle} />
        <a
          className="sp-handle"
          href={profileUrlFor(platform, creator.handle)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <PlatformGlyph platform={platform} />
          @{creator.handle}
        </a>
      </div>
    );
    return (
      <DetailPage
        title={creator.title ?? `@${creator.handle}`}
        onBack={() => (creator.handle === initialCreator ? onBack() : setOpenId(null))}
        action={
          library && (
            <button
              className="tier-filter-toggle"
              onClick={library.onToggleView}
              aria-label={grid ? 'Switch to list view' : 'Switch to grid view'}
              title={grid ? 'List view' : 'Grid view'}
            >
              {grid ? <Rows3 size={16} /> : <Grid2x2 size={16} />}
            </button>
          )
        }
      >
        <div className="sp-scroll">
          {/* The catalog needs a signed-in read, so an empty list before it
              lands means "not here yet" rather than "nothing of theirs". */}
          {list.length === 0 && !loading && (
            <div className="rp-empty sp-empty">Nothing of theirs in Gnosis yet.</div>
          )}
          {grid ? (
            /* The creator takes two of the grid's three columns and the first
               clip takes the third, so the person and their work are one block
               rather than a panel sitting on top of a grid. */
            <TechniqueGrid list={list} library={library} lead={head} />
          ) : (
            <>
              {head}
              <div className="sp-list">
                {groupTechniques(list).map((g) => { const t = g.lead; return (
                  <TechniqueCard
                    key={g.key}
                    id={t.id}
                    name={t.name}
                    japaneseName={t.japaneseName}
                    type={t.type}
                    status={library?.statusOf(t.id) ?? TechniqueStatus.Unchecked}
                    categoryColor={TIER_ACCENT_COLORS[t.tier as BeltRank]}
                    instagramUrl={t.instagramUrl}
                    author={t.author}
                    attribution={t.attribution}
                    legality={legalityNote(t, rules)}
                    rule={ruleFor(t, rules)}
                    watched={library?.isWatched(t) ?? false}
                    onPipClick={library ? () => library.onPlay(t) : undefined}
                    onToggleMat={library ? () => library.onToggleMat(t) : undefined}
                    onInfoClick={library ? () => library.onInfo(t) : undefined}
                    variantCount={g.variants.length}
                    onVariantsClick={library ? () => library.onVersions(g) : undefined}
                    onOpenAllVersions={library ? () => library.onOpenAllVersions(g) : undefined}
                  />
                ); })}
              </div>
            </>
          )}
        </div>
      </DetailPage>
    );
  }

  const rows = ranked.map(([handle, list]) => {
    const up = WRITTEN_UP.get(handle);
    return { id: handle, handle, n: list.length, name: up?.title ?? `@${handle}`, sub: up ? `@${handle}` : '' };
  });

  return (
    <DetailPage title={title} icon={icon} onBack={onBack}>
      <div className="home-panel-body">
        {/* Column headers, so the bare number on the right is labelled once
            rather than repeating a unit on every row. "In Gnosis" rather than
            "accepted": nobody submitted anything, the clips were picked out of
            their reels, so a word implying an application would misdescribe it. */}
        {rows.length > 0 && (
          <div className="sp-thead">
            <span>Practitioner</span>
            <span>Clips in Gnosis</span>
          </div>
        )}
        {/* The rows come off the catalog, which needs a signed-in read, so an
            empty list means "not here yet" rather than "nobody qualifies". */}
        {rows.length === 0 && <div className="rp-empty sp-empty">{loading ? 'Loading…' : 'No creators yet.'}</div>}
        <ul className="forum-topic-list">
          {rows.map((r) => (
            <li key={r.id}>
              <button className="forum-topic-row" onClick={() => setOpenId(r.id)}>
                <Face className="sp-avatar" handle={r.handle} />
                <div className="forum-topic-main">
                  <span className="forum-topic-name">{r.name}</span>
                  {r.sub && <span className="forum-topic-snippet">{r.sub}</span>}
                </div>
                {/* The clip count in the reel boards' own number style, held to
                    the right edge so the figures line up as a standing down the
                    list instead of tracking the ragged ends of the names. */}
                {counted && <b className="sp-rank">{r.n}</b>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </DetailPage>
  );
}
